
// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Throw error if environment variables are not set
if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  console.error('❌ Missing Supabase environment variables!');
  console.error('Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Vercel dashboard');
  throw new Error('Supabase environment variables not configured');
}

/**
 * "Remember me" storage selector.
 *
 * The login form (src/pages/Auth.tsx) writes the user's "Remember me"
 * choice to localStorage under `ob_remember_me` BEFORE calling signIn().
 * This custom storage reads that flag at every storage operation and
 * routes the Supabase auth session to:
 *   - localStorage    when remember_me !== 'false'   (default — 30-day
 *                     refresh-token-backed session, survives tab close
 *                     and browser restart)
 *   - sessionStorage  when remember_me === 'false'   (session ends when
 *                     the last tab/browser closes)
 *
 * Fallback: if window/storage aren't available (SSR), returns a no-op
 * storage so the client doesn't crash.
 */
const REMEMBER_ME_KEY = 'ob_remember_me'

function isRemembered(): boolean {
  if (typeof window === 'undefined') return true
  try {
    return window.localStorage.getItem(REMEMBER_ME_KEY) !== 'false'
  } catch {
    return true
  }
}

const rememberMeStorage = {
  getItem(key: string): string | null {
    if (typeof window === 'undefined') return null
    try {
      return (isRemembered() ? window.localStorage : window.sessionStorage).getItem(key)
    } catch {
      return null
    }
  },
  setItem(key: string, value: string): void {
    if (typeof window === 'undefined') return
    try {
      if (isRemembered()) {
        window.localStorage.setItem(key, value)
        // Also clear any stale session-storage copy from a previous
        // "don't remember" login on this browser.
        window.sessionStorage.removeItem(key)
      } else {
        window.sessionStorage.setItem(key, value)
        // Remove any previously-persisted localStorage copy so closing
        // the browser genuinely ends the session.
        window.localStorage.removeItem(key)
      }
    } catch {
      // Quota / privacy mode — fail silently rather than crash the app.
    }
  },
  removeItem(key: string): void {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(key)
      window.sessionStorage.removeItem(key)
    } catch {
      /* ignore */
    }
  },
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // See rememberMeStorage above.
    storage: rememberMeStorage,
  },
});
